import { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Table,
  Button,
  Badge,
  Group,
  Text,
  ActionIcon,
  Tooltip,
  Modal,
  Stack,
  Switch,
  TextInput,
  Select,
  PasswordInput,
  LoadingOverlay,
  Box,
} from '@mantine/core';
import {
  IconUserPlus,
  IconEdit,
  IconTrash,
  IconKey,
  IconArrowLeft,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '@/services/users.api';
import { validatePassword, validatePasswordMatch } from '@/utils/validation';
import { showError, showSuccess } from '@/utils/errorHandler';
import type { User, UserRole, CreateUserRequest, UpdateUserRequest } from '@/types/user.types';

const roleLabels: Record<UserRole, string> = {
  admin: 'Administrador',
  analyst: 'Analista',
  stakeholder: 'Stakeholder',
};

const roleColors: Record<UserRole, string> = {
  admin: 'red',
  analyst: 'blue',
  stakeholder: 'gray',
};

export const AdminPanel = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'stakeholder' as UserRole,
    isActive: true,
  });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (error) {
      showError(error, 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      showError(new Error('Preencha todos os campos obrigatórios'));
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      showError(new Error(passwordValidation.error));
      return;
    }

    try {
      setSubmitting(true);
      const userData: CreateUserRequest = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        isActive: formData.isActive,
      };

      await usersApi.create(userData);

      showSuccess('Usuário criado com sucesso');

      setCreateModalOpen(false);
      resetForm();
      loadUsers();
    } catch (error) {
      showError(error, 'Erro ao criar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    if (!formData.name || !formData.email) {
      showError(new Error('Nome e email são obrigatórios'));
      return;
    }

    try {
      setSubmitting(true);
      const userData: UpdateUserRequest = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        isActive: formData.isActive,
      };

      await usersApi.update(selectedUser.id, userData);

      showSuccess('Usuário atualizado com sucesso');

      setEditModalOpen(false);
      setSelectedUser(null);
      resetForm();
      loadUsers();
    } catch (error) {
      showError(error, 'Erro ao atualizar usuário');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = (user: User) => {
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await usersApi.delete(userToDelete.id);
      showSuccess('Usuário deletado com sucesso');
      setDeleteModalOpen(false);
      setUserToDelete(null);
      loadUsers();
    } catch (error) {
      showError(error, 'Erro ao deletar usuário');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;

    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.isValid) {
      showError(new Error(passwordValidation.error));
      return;
    }

    const matchValidation = validatePasswordMatch(newPassword, confirmPassword);
    if (!matchValidation.isValid) {
      showError(new Error(matchValidation.error));
      return;
    }

    try {
      setSubmitting(true);
      await usersApi.resetPassword(selectedUser.id, { newPassword });

      showSuccess('Senha resetada com sucesso');

      setResetPasswordModalOpen(false);
      setSelectedUser(null);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      showError(error, 'Erro ao resetar senha');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      isActive: user.isActive,
    });
    setEditModalOpen(true);
  };

  const openResetPasswordModal = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setConfirmPassword('');
    setResetPasswordModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'stakeholder',
      isActive: true,
    });
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => navigate('/dashboard')}
            >
              <IconArrowLeft size={20} />
            </ActionIcon>
            <Title order={2}>Gerenciamento de Usuários</Title>
          </Group>
          <Button
            leftSection={<IconUserPlus size={18} />}
            onClick={() => setCreateModalOpen(true)}
          >
            Novo Usuário
          </Button>
        </Group>

        <Paper shadow="sm" p="md" radius="md" pos="relative">
          <LoadingOverlay visible={loading} />

          {users.length === 0 && !loading ? (
            <Box ta="center" py="xl">
              <Text c="dimmed">Nenhum usuário encontrado</Text>
            </Box>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Nome</Table.Th>
                  <Table.Th>E-mail</Table.Th>
                  <Table.Th ta="center">Perfil</Table.Th>
                  <Table.Th ta="center">Status</Table.Th>
                  <Table.Th ta="center" style={{ width: 150 }}>Ações</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {users.map((user) => (
                  <Table.Tr key={user.id}>
                    <Table.Td>{user.name}</Table.Td>
                    <Table.Td>{user.email}</Table.Td>
                    <Table.Td ta="center">
                      <Badge color={roleColors[user.role]} variant="light">
                        {roleLabels[user.role]}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="center">
                      <Badge color={user.isActive ? 'green' : 'gray'} variant="dot">
                        {user.isActive ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="center">
                        <Tooltip label="Editar">
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            onClick={() => openEditModal(user)}
                          >
                            <IconEdit size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Resetar Senha">
                          <ActionIcon
                            variant="subtle"
                            color="yellow"
                            onClick={() => openResetPasswordModal(user)}
                          >
                            <IconKey size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Deletar">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => openDeleteModal(user)}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>
      </Stack>

      {/* Modal: Criar Usuário */}
      <Modal
        opened={createModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          resetForm();
        }}
        title="Criar Novo Usuário"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Nome"
            placeholder="Nome completo"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <TextInput
            label="Email"
            placeholder="email@exemplo.com"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />

          <PasswordInput
            label="Senha"
            placeholder="Mínimo 8 caracteres"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
          />

          <Select
            label="Perfil"
            value={formData.role}
            onChange={(value) => setFormData({ ...formData, role: value as UserRole })}
            data={[
              { value: 'admin', label: 'Administrador' },
              { value: 'analyst', label: 'Analista' },
              { value: 'stakeholder', label: 'Stakeholder' },
            ]}
            required
          />

          <Switch
            label="Usuário ativo"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setCreateModalOpen(false);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleCreateUser} loading={submitting}>
              Criar Usuário
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Editar Usuário */}
      <Modal
        opened={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUser(null);
          resetForm();
        }}
        title="Editar Usuário"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Nome"
            placeholder="Nome completo"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <TextInput
            label="Email"
            placeholder="email@exemplo.com"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />

          <Select
            label="Perfil"
            value={formData.role}
            onChange={(value) => setFormData({ ...formData, role: value as UserRole })}
            data={[
              { value: 'admin', label: 'Administrador' },
              { value: 'analyst', label: 'Analista' },
              { value: 'stakeholder', label: 'Stakeholder' },
            ]}
            required
          />

          <Switch
            label="Usuário ativo"
            checked={formData.isActive}
            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setEditModalOpen(false);
                setSelectedUser(null);
                resetForm();
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleUpdateUser} loading={submitting}>
              Salvar Alterações
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Resetar Senha */}
      <Modal
        opened={resetPasswordModalOpen}
        onClose={() => {
          setResetPasswordModalOpen(false);
          setSelectedUser(null);
          setNewPassword('');
          setConfirmPassword('');
        }}
        title={`Resetar Senha - ${selectedUser?.name}`}
        size="md"
      >
        <Stack gap="md">
          <PasswordInput
            label="Nova Senha"
            placeholder="Mínimo 8 caracteres"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />

          <PasswordInput
            label="Confirmar Senha"
            placeholder="Digite a senha novamente"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setResetPasswordModalOpen(false);
                setSelectedUser(null);
                setNewPassword('');
                setConfirmPassword('');
              }}
            >
              Cancelar
            </Button>
            <Button onClick={handleResetPassword} loading={submitting} color="yellow">
              Resetar Senha
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Modal: Confirmar Exclusão */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setUserToDelete(null);
        }}
        title="Confirmar Exclusão"
        size="sm"
      >
        <Stack gap="md">
          <Text>
            Tem certeza que deseja deletar o usuário <Text span fw={600}>"{userToDelete?.name}"</Text>?
          </Text>
          <Group justify="flex-end">
            <Button
              variant="subtle"
              onClick={() => {
                setDeleteModalOpen(false);
                setUserToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button color="red" onClick={handleDeleteUser}>
              Deletar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};
